<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useReportNarrative } from 'dashboard/composables/useReportNarrative';
import ReportVerdict from 'dashboard/components-next/reports/ui/ReportVerdict.vue';

const props = defineProps({
  signals: {
    type: Array,
    default: () => [],
  },
  // frase de veredito pronta, quando a tela sabe algo que os signals nao dizem (ex.: poucas respostas no NPS)
  verdict: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const { summaryFor, pillFor } = useReportNarrative();

const narrative = computed(() => summaryFor(props.signals));
const hasSignals = computed(() => (props.signals || []).length > 0);

const ICONS = { highlight: '🏆', attention: '⚠️', allGood: '👍' };
</script>

<template>
  <!-- A frase de veredito sobe para logo abaixo do cabecalho (ReportHeader); sem signals
       (carregando ou periodo vazio) ela nao aparece, para nao julgar o que ainda nao chegou. -->
  <Teleport v-if="hasSignals" defer to="#report-verdict">
    <ReportVerdict :signals="signals" :verdict="verdict" />
  </Teleport>
  <section
    v-if="hasSignals"
    class="relative hover:z-30 focus-within:z-30 rounded-[22px] border border-n-weak bg-n-solid-2/80 backdrop-blur-xl shadow-[inset_0_1px_0_rgb(255_255_255/0.7),0_14px_36px_rgb(16_24_40/0.08)] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.06),0_14px_36px_rgb(0_0_0/0.4)] p-5"
  >
    <span class="text-sm font-medium text-n-slate-11">
      {{ t('REPORT.NARRATIVE.EYEBROW') }}
    </span>
    <div class="mt-4 grid gap-4 grid-cols-1 sm:grid-cols-2">
      <div
        v-if="narrative.highlight"
        class="rounded-[18px] border border-n-teal-6 bg-n-teal-2/60 p-4"
      >
        <div class="flex items-center gap-2">
          <span class="text-lg">{{ ICONS.highlight }}</span>
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('REPORT.NARRATIVE.HIGHLIGHT_TITLE') }}
          </p>
        </div>
        <p class="mt-2 text-sm font-medium text-n-slate-12">
          {{ narrative.highlight.label }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ narrative.highlight.phrase }}
        </p>
      </div>

      <div
        v-if="narrative.attention"
        class="rounded-[18px] border border-n-amber-6 bg-n-amber-2/60 p-4"
      >
        <div class="flex items-center gap-2">
          <span class="text-lg">{{ ICONS.attention }}</span>
          <p class="text-sm font-semibold text-n-slate-12">
            {{ t('REPORT.NARRATIVE.ATTENTION_TITLE') }}
          </p>
          <span
            v-if="pillFor(narrative.attention.signal)"
            class="text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium"
            :class="pillFor(narrative.attention.signal).colorClass"
          >
            <span>{{ pillFor(narrative.attention.signal).icon }}</span>
            <span>{{ pillFor(narrative.attention.signal).label }}</span>
          </span>
        </div>
        <p class="mt-2 text-sm font-medium text-n-slate-12">
          {{ narrative.attention.label }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ narrative.attention.phrase }}
        </p>
      </div>

      <div
        v-else-if="narrative.allGood"
        class="rounded-[18px] border border-n-teal-6 bg-n-teal-2/60 p-4 flex items-center gap-2"
      >
        <span class="text-lg">{{ ICONS.allGood }}</span>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('REPORT.NARRATIVE.ALL_GOOD') }}
        </p>
      </div>
    </div>
  </section>
</template>
